import { Fragment, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import Layout from './Layout';

export default function RoleDashboardShell({
  title,
  subtitle,
  brandTitle,
  brandSubtitle,
  brandMark = '360',
  menuItems = [],
  menuGroups = [],
  isValidMenuId,
  defaultMenuId = 'overview',
  loading = false,
  error = '',
  children,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const initialMenu = isValidMenuId?.(tabFromUrl) ? tabFromUrl : defaultMenuId;

  const findMenuGroup = (menuId) =>
    menuGroups.find((group) => group.items.some((item) => item.id === menuId))?.label || menuGroups[0]?.label;

  const [activeMenu, setActiveMenuState] = useState(initialMenu);
  const [expandedGroups, setExpandedGroups] = useState(() => ({
    [findMenuGroup(initialMenu)]: true,
  }));

  const setActiveMenu = (id) => {
    if (isValidMenuId && !isValidMenuId(id)) return;
    setActiveMenuState(id);
    const groupLabel = findMenuGroup(id);
    if (groupLabel) {
      setExpandedGroups((prev) => ({ ...prev, [groupLabel]: true }));
    }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', id);
      return next;
    });
  };

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (!isValidMenuId?.(tab)) return;
    setActiveMenuState(tab);
    const groupLabel =
      menuGroups.find((group) => group.items.some((item) => item.id === tab))?.label || menuGroups[0]?.label;
    if (groupLabel) {
      setExpandedGroups((prev) => ({ ...prev, [groupLabel]: true }));
    }
  }, [searchParams, isValidMenuId, menuGroups]);

  const overviewItem = useMemo(
    () => menuItems.find((item) => item.id === 'overview') || menuItems[0],
    [menuItems]
  );

  return (
    <Layout wide>
      <div className="admin-page space-y-6">
        <header className="admin-header">
          <div>
            <h1 className="admin-title">{title}</h1>
            {subtitle ? <p className="admin-subtitle">{subtitle}</p> : null}
          </div>
        </header>

        {error ? (
          <div className="alert-warning rounded-xl" role="alert">
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="admin-loading">
            <span className="inline-block w-8 h-8 border-2 border-slate-300 border-t-indigo-500 rounded-full animate-spin" />
            <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Loading dashboard…</span>
          </div>
        ) : (
          <div className="admin-shell text-left">
            <aside className="admin-nav-wrap">
              <button
                type="button"
                className={`admin-nav-brand ${activeMenu === overviewItem?.id ? 'active' : ''}`}
                onClick={() => setActiveMenu(overviewItem?.id || defaultMenuId)}
              >
                <span className="admin-nav-brand-mark">{brandMark}</span>
                <div>
                  <p className="admin-nav-brand-title">{brandTitle}</p>
                  <p className="admin-nav-brand-subtitle">{brandSubtitle}</p>
                </div>
              </button>
              <nav className="admin-nav" aria-label={`${title} sections`}>
                {menuGroups.map((group) => (
                  <Fragment key={group.label}>
                    <div className="admin-nav-group">
                      <button
                        type="button"
                        className={`admin-nav-group-button ${expandedGroups[group.label] ? 'expanded' : ''}`}
                        onClick={() => setExpandedGroups((prev) => ({ ...prev, [group.label]: !prev[group.label] }))}
                      >
                        <span>{group.label}</span>
                        <span className="admin-nav-chevron" aria-hidden="true">
                          <ChevronDownIcon className="h-4 w-4" />
                        </span>
                      </button>
                      {expandedGroups[group.label] ? (
                        <div className="admin-nav-subitems">
                          {group.items.map((item) => {
                            const MenuIcon = item.icon;
                            return (
                              <button
                                key={item.id}
                                type="button"
                                onClick={() => setActiveMenu(item.id)}
                                className={`admin-nav-tab ${activeMenu === item.id ? 'active' : ''}`}
                              >
                                <span className="admin-nav-thumb" aria-hidden="true">
                                  {MenuIcon ? <MenuIcon className="h-4 w-4" /> : item.label.slice(0, 1)}
                                </span>
                                <span className="admin-nav-label">{item.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      ) : null}
                    </div>
                  </Fragment>
                ))}
              </nav>
            </aside>
            <div className="admin-content">
              {typeof children === 'function' ? children({ activeMenu, setActiveMenu }) : children}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
